/**
 * Код ANZONIX: счёт рядов.
 *
 * Две формы одних и тех же функций. Целиком по истории — когда
 * аргументы известны на всех свечах сразу (быстро). И по шагам — когда
 * аргумент считается свеча за свечой из памяти кода: тогда у вызова своя
 * история, и функция видит её так же, как увидела бы ряд целиком.
 */
export const truth = (x) => Number.isFinite(x) && x !== 0;
export function series(value, length) {
    if (typeof value !== 'number')
        return value;
    return new Float64Array(length).fill(value);
}
export function map2(a, b, length, f) {
    if (typeof a === 'number' && typeof b === 'number')
        return f(a, b);
    const out = new Float64Array(length);
    const sa = series(a, length);
    const sb = series(b, length);
    for (let i = 0; i < length; i += 1)
        out[i] = f(sa[i], sb[i]);
    return out;
}
export function map1(a, length, f) {
    if (typeof a === 'number')
        return f(a);
    const out = new Float64Array(length);
    for (let i = 0; i < length; i += 1)
        out[i] = f(a[i]);
    return out;
}
export function shift(a, back, length) {
    if (typeof a === 'number')
        return a;
    const out = new Float64Array(length).fill(NaN);
    for (let i = back; i < length; i += 1)
        out[i] = a[i - back];
    return out;
}
export function windowed(a, period, f) {
    const out = new Float64Array(a.length).fill(NaN);
    const n = Math.max(1, Math.floor(period));
    for (let i = n - 1; i < a.length; i += 1)
        out[i] = windowAt(a, i + 1, n, f);
    return out;
}
/** Окно из `period` последних значений до `end` (не включая). Пропуск внутри — пусто. */
export function windowAt(a, end, period, f) {
    const n = Math.max(1, Math.floor(period));
    if (end < n)
        return NaN;
    const slice = [];
    for (let j = end - n; j < end; j += 1) {
        const value = a[j];
        if (!Number.isFinite(value))
            return NaN;
        slice.push(value);
    }
    return f(slice);
}
export const WINDOW = {
    sma: (slice) => slice.reduce((sum, x) => sum + x, 0) / slice.length,
    sum: (slice) => slice.reduce((sum, x) => sum + x, 0),
    highest: (slice) => Math.max(...slice),
    lowest: (slice) => Math.min(...slice),
    wma: (slice) => {
        let weighted = 0;
        let weights = 0;
        slice.forEach((x, i) => {
            weighted += x * (i + 1);
            weights += i + 1;
        });
        return weighted / weights;
    },
    stdev: (slice) => {
        const mean = slice.reduce((sum, x) => sum + x, 0) / slice.length;
        return Math.sqrt(slice.reduce((sum, x) => sum + (x - mean) ** 2, 0) / slice.length);
    },
};
export const emaState = () => ({ seed: 0, count: 0, previous: NaN });
export function emaStep(state, value, period) {
    const n = Math.max(1, Math.floor(period));
    if (!Number.isFinite(value)) {
        state.count = 0;
        state.seed = 0;
        state.previous = NaN;
        return NaN;
    }
    if (!Number.isFinite(state.previous)) {
        state.seed += value;
        state.count += 1;
        if (state.count === n) {
            state.previous = state.seed / n;
            return state.previous;
        }
        return NaN;
    }
    state.previous = value * (2 / (n + 1)) + state.previous * (1 - 2 / (n + 1));
    return state.previous;
}
export function emaOf(a, period) {
    const out = new Float64Array(a.length);
    const state = emaState();
    for (let i = 0; i < a.length; i += 1)
        out[i] = emaStep(state, a[i], period);
    return out;
}
export const rsiState = () => ({ gain: 0, loss: 0, count: 0, last: NaN, started: false });
export function rsiStep(state, value, period) {
    const n = Math.max(1, Math.floor(period));
    const before = state.last;
    const first = !state.started;
    state.started = true;
    state.last = value;
    if (first)
        return NaN;
    const change = value - before;
    if (!Number.isFinite(change))
        return NaN;
    const up = Math.max(0, change);
    const down = Math.max(0, -change);
    if (state.count < n) {
        state.gain += up;
        state.loss += down;
        state.count += 1;
        if (state.count === n) {
            state.gain /= n;
            state.loss /= n;
            return state.loss === 0 ? 100 : 100 - 100 / (1 + state.gain / state.loss);
        }
        return NaN;
    }
    state.gain = (state.gain * (n - 1) + up) / n;
    state.loss = (state.loss * (n - 1) + down) / n;
    return state.loss === 0 ? 100 : 100 - 100 / (1 + state.gain / state.loss);
}
export function rsiOf(a, period) {
    const out = new Float64Array(a.length);
    const state = rsiState();
    for (let i = 0; i < a.length; i += 1)
        out[i] = rsiStep(state, a[i], period);
    return out;
}
/** Сколько свечей прошло с последней, где условие было верным; ещё не было — пусто. */
export function barsSinceOf(condition) {
    const out = new Float64Array(condition.length);
    let last = -1;
    for (let i = 0; i < condition.length; i += 1) {
        if (truth(condition[i]))
            last = i;
        out[i] = last < 0 ? NaN : i - last;
    }
    return out;
}
/** Значение ряда на свече, где условие было верным в n-й раз от конца (0 — последний). */
export function valueWhenOf(condition, source, occurrence) {
    const out = new Float64Array(condition.length).fill(NaN);
    const seen = [];
    const keep = Math.max(0, Math.floor(occurrence));
    for (let i = 0; i < condition.length; i += 1) {
        if (truth(condition[i])) {
            seen.push(source[i]);
            if (seen.length > keep + 1)
                seen.shift();
        }
        if (seen.length > keep)
            out[i] = seen[seen.length - 1 - keep];
    }
    return out;
}
/**
 * Свинг: свеча, выше (ниже) которой нет ни `left` свечей слева, ни
 * `right` справа. Известен только через `right` свечей — поэтому
 * значение стоит на свече подтверждения, а не на самой вершине: иначе
 * код заглядывал бы в будущее.
 */
export function pivotOf(values, left, right, high) {
    const out = new Float64Array(values.length).fill(NaN);
    const l = Math.max(1, Math.floor(left));
    const r = Math.max(1, Math.floor(right));
    for (let i = l + r; i < values.length; i += 1) {
        const center = i - r;
        const value = values[center];
        if (!Number.isFinite(value))
            continue;
        let ok = true;
        for (let j = center - l; j <= center + r && ok; j += 1) {
            if (j === center)
                continue;
            const other = values[j];
            // Слева — строго, справа — нестрого: из двух равных вершин свингом считается первая.
            if (high ? (j < center ? other > value : other >= value) : j < center ? other < value : other <= value)
                ok = false;
        }
        if (ok)
            out[i] = value;
    }
    return out;
}
/** Точечные функции: результат зависит только от текущих значений аргументов. */
export const POINT = {
    abs: ([x]) => Math.abs(x),
    max: ([a, b]) => Math.max(a, b),
    min: ([a, b]) => Math.min(a, b),
    iff: ([c, a, b]) => (truth(c) ? a : b),
    sqrt: ([x]) => (x < 0 ? NaN : Math.sqrt(x)),
    pow: ([x, y]) => Math.pow(x, y),
    log: ([x]) => (x <= 0 ? NaN : Math.log(x)),
    round: ([x, digits]) => {
        const k = 10 ** Math.max(0, Math.min(10, Math.floor(digits ?? 0)));
        return Math.round(x * k) / k;
    },
    floor: ([x]) => Math.floor(x),
    ceil: ([x]) => Math.ceil(x),
    sign: ([x]) => (Number.isFinite(x) ? Math.sign(x) : NaN),
    isna: ([x]) => (Number.isFinite(x) ? 0 : 1),
    nz: ([x, replacement]) => (Number.isFinite(x) ? x : replacement ?? 0),
};
/* ---------------------------------------------------------------- периоды и VWAP */
/** Сутки — код периода по умолчанию. */
export const DAY_PERIOD = 86_400_000;
/**
 * Код периода: положительный — длина в миллисекундах (минуты, часы, сутки);
 * −n — n недель (с понедельника 00:00 UTC); −1000 − n — n месяцев.
 */
export function scriptPeriodOf(text) {
    const trimmed = text.trim();
    // Число, как в Pine, — минуты: "60", "240".
    if (/^\d+$/.test(trimmed))
        return Number(trimmed) > 0 ? Number(trimmed) * 60_000 : null;
    // Заглавная «M» — месяц, как в Pine; строчная «m» — минуты.
    const pineMonth = /^(\d*)M$/.exec(trimmed);
    if (pineMonth)
        return -1000 - Number(pineMonth[1] || '1');
    const match = /^(\d*)\s*([a-zа-яё]+)$/.exec(trimmed.toLowerCase());
    if (!match)
        return null;
    const count = Number(match[1] || '1');
    if (!(count > 0))
        return null;
    const unit = match[2];
    if (['m', 'min', 'м', 'мин', 'минута'].includes(unit))
        return count * 60_000;
    if (['h', 'ч', 'час'].includes(unit))
        return count * 3_600_000;
    if (['d', 'д', 'дн', 'день', 'сутки'].includes(unit))
        return count * DAY_PERIOD;
    if (['w', 'н', 'нед', 'неделя'].includes(unit))
        return -count;
    if (['mo', 'mon', 'мес', 'месяц'].includes(unit))
        return -1000 - count;
    return null;
}
/** Номер периода, в который попадает время: свечи одного периода — один номер. */
export function periodIndex(time, code) {
    if (code > 0)
        return Math.floor(time / code);
    if (code > -1000)
        return Math.floor((time - 345_600_000) / (-code * 604_800_000));
    const date = new Date(time);
    return Math.floor((date.getUTCFullYear() * 12 + date.getUTCMonth()) / (-code - 1000));
}
export const vwapState = () => ({ pv: 0, pv2: 0, v: 0 });
/** Шаг VWAP: якорь верен — счёт заново с этой свечи. `deviation` — отклонение вместо самой средней. */
export function vwapStep(state, price, volume, anchor, deviation) {
    if (anchor) {
        state.pv = 0;
        state.pv2 = 0;
        state.v = 0;
    }
    if (Number.isFinite(price) && Number.isFinite(volume) && volume > 0) {
        state.pv += price * volume;
        state.pv2 += price * price * volume;
        state.v += volume;
    }
    if (state.v <= 0)
        return NaN;
    const mean = state.pv / state.v;
    return deviation ? Math.sqrt(Math.max(0, state.pv2 / state.v - mean * mean)) : mean;
}
//# sourceMappingURL=script-math.js.map